import {
  formatDateToMMDDYYYY,
  RoutineWorkflow,
  useDeleteMutation,
  useGetQuery,
  useRoutineAlerts,
  useUpdateMutation,
  useWorkspaceRoutine,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import LoadingIndicator from '../../loadingIndicator';
import { AlertForm } from './alertForm';
import { excludedConfigKeys } from './alertSummary';
import { getAlertType, getCounterRoutinesForOnInterval } from './helpers';
import { AlertType } from './types';

type EditAlertFormProps = {
  onClose: () => void;
  alertId: string;
  routineId: string;
  workspaceAssetId?: string;
  productId?: string;
  groupId?: string;
};

export const EditAlertForm = ({
  onClose,
  alertId,
  routineId,
  workspaceAssetId,
  productId,
  groupId,
}: EditAlertFormProps) => {
  const { mutateAsync: updateWorkflow } = useUpdateMutation({
    modelName: 'workflow',
    query: UPDATE_WORKFLOW,
  });

  const { mutateAsync: deleteWorkflow } = useDeleteMutation({
    modelName: 'workflow',
    query: DELETE_WORKFLOW,
  });

  const { data: workflowData, isLoading: isWorkflowLoading } = useGetQuery({
    modelName: 'workflow',
    query: GET_WORKFLOW,
    id: alertId,
  });

  const routineData = useWorkspaceRoutine(routineId);

  const { workspaceAsset, isLoading: isAssetLoading } = useRoutineAlerts(
    workspaceAssetId || '',
    routineId,
    productId
  );

  const isLoading = isWorkflowLoading || isAssetLoading;

  const [workflow, setWorkflow] = useState<RoutineWorkflow>();
  const [defaultObject, setDefaultObject] = useState<any>(null);
  const [counterRoutines, setCounterRoutines] = useState<WorkspaceRoutine[]>(
    []
  );

  useEffect(() => {
    setWorkflow(undefined);
    setDefaultObject(null);
    setCounterRoutines([]);
  }, [alertId]);

  useEffect(() => {
    if (!workflow && !isLoading) {
      const routineWorkflow = workflowData as RoutineWorkflow;

      if (!routineWorkflow?.trigger?.config) {
        return;
      }

      // For entity alerts, counter routines (based on asset completion counts) aren't available
      const counterRoutines = workspaceAsset
        ? getCounterRoutinesForOnInterval(workspaceAsset)
        : [];
      setCounterRoutines(counterRoutines);

      const configKeys = Object.keys(routineWorkflow.trigger.config);

      const alertKeys = configKeys.filter(
        (key) => !excludedConfigKeys.includes(key)
      );
      const alertType = getAlertType(alertKeys[0]);
      const secondAlertType =
        (alertKeys.length > 1 && getAlertType(alertKeys[1])) || undefined;
      const minValue = routineWorkflow?.trigger?.config?.ceiling;
      const maxValue = routineWorkflow?.trigger?.config?.floor;
      const dayInterval = routineWorkflow?.trigger?.config?.dayInterval;
      const dayIntervalUnit = routineWorkflow?.trigger?.config?.dayIntervalUnit;
      const startDateValue = formatDateToMMDDYYYY(
        routineWorkflow?.trigger?.config?.startDate
      );
      const completionsValue =
        routineWorkflow?.trigger?.config?.onCompletionCount;
      const daysSkippedValue =
        routineWorkflow?.trigger?.config?.daysSinceCompleted;
      const onDateValue = formatDateToMMDDYYYY(
        routineWorkflow?.trigger?.config?.onDate
      );
      const onIntervalValue = routineWorkflow?.trigger?.config?.onInterval;
      const onIntervalStartValue =
        routineWorkflow?.trigger?.config?.onIntervalStart;
      const onIntervalCounterId =
        routineWorkflow?.trigger?.config?.triggerCounterRoutineId;
      const startDate = formatDateToMMDDYYYY(
        routineWorkflow?.disabledBetween?.start
      );
      const endDate = formatDateToMMDDYYYY(
        routineWorkflow?.disabledBetween?.end
      );

      const getInputValue = (alertType: AlertType | undefined) => {
        switch (alertType) {
          case AlertType.DaysSkipped:
            return daysSkippedValue;

          case AlertType.RoutineCompletions:
            return completionsValue;

          case AlertType.Min:
            return minValue;

          case AlertType.Max:
            return maxValue;

          default:
            return undefined;
        }
      };

      const defaultValues = {
        alertLabel: routineWorkflow?.name || '',
        alertLabelPlaceholder: routineWorkflow?.name || '',
        startDate: startDate,
        endDate: endDate,
        alertType: alertType,
        secondAlertType: secondAlertType,
        inputField: getInputValue(alertType),
        secondInputField: getInputValue(secondAlertType),
        minValue: minValue,
        maxValue: maxValue,
        completions: completionsValue?.toString(),
        daysSkipped: daysSkippedValue?.toString(),
        onInterval: onIntervalValue?.toString(),
        onIntervalStart: onIntervalStartValue?.toString(),
        intervalRoutineId: onIntervalCounterId,
        onDate: onDateValue,
        dayInterval,
        dayIntervalUnit,
        daysStartDate: startDateValue,
      };

      setDefaultObject(defaultValues);
      setWorkflow(routineWorkflow);
    }
  }, [workflowData, isLoading, workflow, workspaceAsset, alertId]);

  const deleteAlert = async () => {
    if (!workflow) return;

    const input = {
      id: workflow.id,
    };

    await deleteWorkflow(
      { input },
      {
        onSettled: () => {
          onClose();
        },
      }
    );
  };

  const submitAlert = async (input: any) => {
    await updateWorkflow(
      { input },
      {
        onSuccess: async () => {
          onClose();
        },
      }
    );
  };

  return isLoading || !defaultObject || !counterRoutines || !workflow ? (
    <LoadingIndicator />
  ) : (
    <AlertForm
      defaultValues={defaultObject}
      routine={routineData}
      workspaceAsset={workspaceAsset}
      product={productId ? { id: productId } : undefined}
      group={groupId ? { id: groupId } : undefined}
      workflowProps={workflow}
      isEdit
      submitAlert={submitAlert}
      deleteAlert={deleteAlert}
      counterRoutines={counterRoutines}
      onClose={onClose}
      headerTitle="alerts.editAlert"
    />
  );
};

const GET_WORKFLOW = `
  query GetWorkflow($id: ID!) {
    getWorkflow(id: $id) {
      id
      name
      group {
        id
      }
      product {
        id
      }
      workspaceAsset {
        id
      }
      trigger {
        type
        config
      }
      action {
        type
        config
      }
      disabled
      disabledBetween {
        start
        end
      }
      workspace {
        id
      }
    }
  }
`;

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
  updateWorkflow(input: $input) {
    id
  }
}
`;

const DELETE_WORKFLOW = `
  mutation DeleteWorkflow($input: MutateWorkflowInput!) {
    deleteWorkflow(input: $input) {
      id
    }
  }
`;
